/**
 * MonthlyCalendar
 *
 * Month grid with weekday header, day cells, and legend.
 * Shared between CalendarView (homepage) and CareScreen.
 */

import { View, Pressable } from "react-native";
import { Text } from "@louise/ui/Text/Text";
import {
  ChevronLeft,
  ChevronRight,
  Droplet,
  Sprout,
} from "lucide-react-native";
import {
  WEEKDAY_LABELS,
  createLocalDateString,
} from "@src/packages/utils/date";
import { CalendarDay } from "./CalendarDay";
import type { DaySummary } from "../calendar.types";
import { usePressCapture } from "@src/packages/analytics/usePressCapture";

export interface MonthlyCalendarProps {
  calendarDays: (number | null)[];
  displayedYear: number;
  displayedMonth: number;
  monthYearHeader: string;
  isToday: (day: number | null) => boolean;
  getDaySummary: (day: number) => DaySummary | undefined;
  onDayPress: (day: number) => void;
  onPreviousMonth: () => void;
  onNextMonth: () => void;
  /** CareScreen: set of YYYY-MM-DD strings for period selection mode */
  selectedPeriodDays?: Set<string>;
  /** CareScreen: shows "Cycle Day X" under month header */
  cycleDay?: number | null;
}

export function MonthlyCalendar({
  calendarDays,
  displayedYear,
  displayedMonth,
  monthYearHeader,
  isToday,
  getDaySummary,
  onDayPress,
  onPreviousMonth,
  onNextMonth,
  selectedPeriodDays,
  cycleDay,
}: MonthlyCalendarProps) {
  const capturePress = usePressCapture("MonthlyCalendar");
  return (
    <>
      {/* Month Header */}
      <View className="relative z-10 mb-4 flex-row items-center justify-between">
        <View>
          <Text className="text-xl font-semibold tracking-tight text-white">
            {monthYearHeader}
          </Text>
          {cycleDay != null && (
            <View className="mt-0.5 flex-row items-center gap-1.5">
              <View className="h-1.5 w-1.5 rounded-full bg-[#4ADE80]" />
              <Text className="text-xs font-medium text-[#4ADE80]">
                Cycle Day {cycleDay}
              </Text>
            </View>
          )}
        </View>
        <View className="flex-row gap-2">
          <Pressable
            onPress={() => {
              capturePress("previous_month");
              onPreviousMonth();
            }}
            className="h-8 w-8 items-center justify-center rounded-full"
          >
            <ChevronLeft size={20} color="#94A3B8" strokeWidth={2.5} />
          </Pressable>
          <Pressable
            onPress={() => {
              capturePress("next_month");
              onNextMonth();
            }}
            className="h-8 w-8 items-center justify-center rounded-full"
          >
            <ChevronRight size={20} color="#94A3B8" strokeWidth={2.5} />
          </Pressable>
        </View>
      </View>

      {/* Weekday Header Row */}
      <View className="mb-3" style={{ flexDirection: "row", gap: 8 }}>
        {WEEKDAY_LABELS.map((day, index) => {
          const isHighlighted = index === 3;
          return (
            <View
              key={day}
              className={`flex-1 rounded-lg py-2 ${
                isHighlighted ? "bg-white/10" : "bg-white/[0.03]"
              }`}
            >
              <Text
                className={`text-center text-[0.65rem] font-semibold tracking-wider ${
                  isHighlighted ? "text-slate-100" : "text-slate-500"
                }`}
              >
                {day}
              </Text>
            </View>
          );
        })}
      </View>

      {/* Calendar Grid */}
      <View style={{ gap: 8 }}>
        {Array.from({ length: Math.ceil(calendarDays.length / 7) }).map(
          (_, rowIndex) => (
            <View key={rowIndex} style={{ flexDirection: "row", gap: 8 }}>
              {calendarDays
                .slice(rowIndex * 7, rowIndex * 7 + 7)
                .map((day, colIndex) => {
                  const summary = day !== null ? getDaySummary(day) : undefined;
                  const dateStr =
                    day !== null
                      ? createLocalDateString(
                          displayedYear,
                          displayedMonth,
                          day,
                        )
                      : "";
                  const isSelectedForPeriod =
                    selectedPeriodDays?.has(dateStr) ?? false;

                  return (
                    <CalendarDay
                      key={colIndex}
                      day={day}
                      summary={summary}
                      isToday={isToday(day)}
                      isSelectedForPeriod={isSelectedForPeriod}
                      onPress={onDayPress}
                    />
                  );
                })}
            </View>
          ),
        )}
      </View>

      {/* Legend */}
      <View className="relative z-10 mb-2 mt-5 flex-row flex-wrap items-center justify-center gap-x-3 gap-y-1.5 px-1">
        <View className="flex-row items-center gap-1.5">
          <View
            className="h-2 w-2 rounded-full bg-[#A78BFA]"
            style={{
              shadowColor: "#A78BFA",
              shadowOffset: { width: 0, height: 0 },
              shadowOpacity: 0.5,
              shadowRadius: 5,
            }}
          />
          <Text className="text-[0.65rem] font-medium text-slate-400">
            Medication
          </Text>
        </View>
        <View className="flex-row items-center gap-1.5">
          <View
            className="h-2 w-2 rounded-full bg-[#2DD4BF]"
            style={{
              shadowColor: "#2DD4BF",
              shadowOffset: { width: 0, height: 0 },
              shadowOpacity: 0.5,
              shadowRadius: 5,
            }}
          />
          <Text className="text-[0.65rem] font-medium text-slate-400">
            Appointment
          </Text>
        </View>
        <View className="flex-row items-center gap-1.5">
          <Droplet size={12} color="#F472B6" fill="#F472B6" />
          <Text className="text-[0.65rem] font-medium text-slate-400">
            Period
          </Text>
        </View>
        <View className="flex-row items-center gap-1.5">
          <Sprout size={12} color="#4ADE80" fill="transparent" />
          <Text className="text-[0.65rem] font-medium text-slate-400">
            Fertile
          </Text>
        </View>
        <View className="flex-row items-center gap-1.5">
          <Sprout size={12} color="#4ADE80" fill="#4ADE80" />
          <Text className="text-[0.65rem] font-medium text-slate-400">
            Ovulation
          </Text>
        </View>
      </View>
      {/* Prediction note — faded markers indicate estimated future days */}
      <View className="relative z-10 mb-6 px-1">
        <Text className="text-center text-[0.6rem] font-medium text-slate-500">
          Faded markers show predicted days
        </Text>
      </View>
    </>
  );
}
